import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Calendar, TrendingUp, Snowflake, Sun, Leaf, Flower } from 'lucide-react';

interface SeasonalPatternsProps {
  seasonalPatterns: Record<string, number>;
  title?: string;
}

const SeasonalPatterns: React.FC<SeasonalPatternsProps> = ({ 
  seasonalPatterns, 
  title = "Seasonal Performance Patterns" 
}) => {
  const getSeasonIcon = (season: string) => {
    switch (season.toLowerCase()) {
      case 'winter': return Snowflake;
      case 'spring': return Flower;
      case 'summer': return Sun;
      case 'fall': return Leaf;
      default: return Calendar;
    }
  };

  const getSeasonColor = (season: string) => {
    switch (season.toLowerCase()) {
      case 'winter': return '#3b82f6';
      case 'spring': return '#10b981';
      case 'summer': return '#f59e0b';
      case 'fall': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // Prepare data for charts
  const pieData = Object.entries(seasonalPatterns).map(([season, count]) => ({
    name: season,
    value: count,
    color: getSeasonColor(season)
  }));

  const barData = Object.entries(seasonalPatterns).map(([season, count]) => ({
    season,
    workshops: count,
    percentage: ((count / Object.values(seasonalPatterns).reduce((sum, val) => sum + val, 0)) * 100).toFixed(1)
  }));

  const totalWorkshops = Object.values(seasonalPatterns).reduce((sum, val) => sum + val, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      // Handle both pie chart (name/value) and bar chart (season/workshops) data structures
      const name = data.name || data.season;
      const value = data.value || data.workshops;
      const percentage = totalWorkshops > 0 ? ((value / totalWorkshops) * 100).toFixed(1) : '0.0';
      
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900">{name}</p>
          <p className="text-blue-600">
            Workshops: {value}
          </p>
          <p className="text-gray-600">
            Percentage: {percentage}%
          </p>
        </div>
      );
    }
    return null;
  };

  const COLORS = pieData.map(item => item.color);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-500" />
          <span className="text-sm text-gray-600">Seasonal Analysis</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Pie Chart */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-4">Workshop Distribution by Season</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name} ${((percent as number) * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bar Chart */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-4">Seasonal Workshop Count</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="season" stroke="#666" fontSize={12} />
                <YAxis stroke="#666" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="workshops" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Season Details */}
      <div>
        <h4 className="font-semibold text-gray-900 mb-4">Seasonal Performance Details</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(seasonalPatterns).map(([season, count]) => {
            const IconComponent = getSeasonIcon(season);
            const color = getSeasonColor(season);
            const percentage = ((count / totalWorkshops) * 100).toFixed(1);
            
            return (
              <div key={season} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <IconComponent className="w-5 h-5" style={{ color }} />
                  <h5 className="font-semibold text-gray-900 capitalize">{season}</h5>
                </div>
                
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 mb-1">{count}</div>
                  <div className="text-sm text-gray-600 mb-3">Workshops</div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div 
                      className="h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${percentage}%`,
                        backgroundColor: color
                      }}
                    ></div>
                  </div>
                  
                  <div className="text-sm font-semibold" style={{ color }}>
                    {percentage}%
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Insights */}
      <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-500" />
          Seasonal Insights
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-700 mb-2">
              <strong>Peak Season:</strong> {
                Object.entries(seasonalPatterns).reduce((max, [season, count]) => 
                  count > max[1] ? [season, count] : max, ['', 0]
                )[0]
              } with {
                Object.entries(seasonalPatterns).reduce((max, [season, count]) => 
                  count > max[1] ? [season, count] : max, ['', 0]
                )[1]
              } workshops
            </p>
            <p className="text-sm text-gray-700">
              <strong>Low Season:</strong> {
                Object.entries(seasonalPatterns).reduce((min, [season, count]) => 
                  count < min[1] ? [season, count] : min, ['', Infinity]
                )[0]
              } with {
                Object.entries(seasonalPatterns).reduce((min, [season, count]) => 
                  count < min[1] ? [season, count] : min, ['', Infinity]
                )[1]
              } workshops
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-700 mb-2">
              <strong>Total Workshops:</strong> {totalWorkshops} across all seasons
            </p>
            <p className="text-sm text-gray-700">
              <strong>Average per Season:</strong> {(totalWorkshops / Object.keys(seasonalPatterns).length).toFixed(1)} workshops
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeasonalPatterns;
